import { FunctionComponent, ReactNode, useContext, useEffect } from "react";
import {
  RefineThemedLayoutV2SiderProps,
  ThemedLayoutV2,
  ThemedSiderV2,
} from "@refinedev/mui";
import { useRefineKbar } from "@refinedev/kbar";
import { useKbarActions } from "../../hooks/useKbarActions";
import { ThemedKbar } from "./ThemedKbar";
import {
  Header,
  HeaderWithTitle,
  HeaderWithTitleAndMarketplace,
} from "./Header";
import { TitleProps } from "@refinedev/core";
import { useInitializeApp } from "../../hooks/useInitializeApp";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReAuthentication } from "../../pages/authentication/ReAuthentication";
import { ReAuthenticationCallback } from "../../pages/authentication/ReAuthenticationCallback";
import { SyncrosaleThemedTitle } from "./SyncrosaleThemedTitle";
import { SyncrosaleLoader } from "./components/SyncrosaleLoader";
import { getRuntimeEnv } from "../../providers/getRuntimeEnv";
import { Box, Divider, IconButton, Stack } from "@mui/material";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import { ColorModeContext } from "../../providers/colorProvider";
import { MarketplaceClock } from "./components/MarketplaceClock";
import { SelectStore } from "../../pages/stores/select-stores/SelectStore";
import { LanguageSelect } from "./LanguageSelect";
import { UserMenu } from "./UserMenu";
import { CurrencyToggle } from "./CurrencyToggle";

interface SyncrosaleLayoutProps {
  children: ReactNode;
}

const MobileSidebarControls = () => {
  const { mode, toggleMode } = useContext(ColorModeContext);
  return (
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        gap: 1,
        mx: 1.5,
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      {/* Clock + Theme toggle */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <MarketplaceClock />
        <Stack direction="row" alignItems="center" gap={1}>
          <CurrencyToggle />
          <IconButton color="inherit" onClick={toggleMode} size="small">
            {mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
          </IconButton>
        </Stack>
      </Stack>

      <Divider />

      {/* Store + Language */}
      <Stack direction="row" alignItems="center" gap={1}>
        <SelectStore compact />
        <LanguageSelect compact />
      </Stack>

      <Divider />

      {/* User */}
      <Box sx={{ py: 0.5 }}>
        <UserMenu />
      </Box>
    </Box>
  );
};

const KbarSetup = () => {
  useRefineKbar();
  useKbarActions();
  return null;
};

// Removes logout button from sidebar
const Sider = (props: RefineThemedLayoutV2SiderProps) => {
  return (
    <ThemedSiderV2
      {...props}
      render={({ items }) => (
        <>
          {items}
          <MobileSidebarControls />
        </>
      )}
    />
  );
};

export const SyncrosaleLayout = ({ children }: SyncrosaleLayoutProps) => {
  const { showLoading, hasStore, needsReAuthentication, invalidMarketplace } =
    useInitializeApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const runtimeEnv = getRuntimeEnv();
  const isDevEnv = runtimeEnv === "dev";
  const title = isDevEnv ? "SyncroDev" : "Syncrosale";
  const logo = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";

  const renderTitle: FunctionComponent<TitleProps> = (props) => (
    <SyncrosaleThemedTitle {...props} text={title} icon={logo} />
  );

  const isReAuthCallback =
    searchParams.get("spapi_oauth_code") &&
    localStorage.getItem("reAuthenticating") === "true";

  useEffect(() => {
    if (
      !hasStore &&
      !showLoading &&
      !needsReAuthentication &&
      window.location.pathname !== "/store/create" &&
      !isReAuthCallback
    ) {
      setTimeout(() => navigate("/store/create"), 100);
    }
  }, [
    hasStore,
    navigate,
    showLoading,
    needsReAuthentication,
    isReAuthCallback,
  ]);

  if (showLoading) {
    return <SyncrosaleLoader />;
  }

  if (isReAuthCallback) {
    return <ReAuthenticationCallback />;
  }

  if (window.location.pathname === "/store/create") {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        {children}
      </ThemedLayoutV2>
    );
  }

  if (needsReAuthentication && invalidMarketplace) {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        <ReAuthentication invalidMarketplaces={[invalidMarketplace]} />
      </ThemedLayoutV2>
    );
  }

  return (
    <>
      <ThemedKbar />
      <KbarSetup />
      <ThemedLayoutV2
        Header={hasStore ? Header : HeaderWithTitle}
        Title={renderTitle}
        Sider={hasStore ? Sider : () => null}
      >
        {children}
      </ThemedLayoutV2>
    </>
  );
};
